import BaseApi, {
    BaseApiResponse,
    cleanParams,
    dangerousAxiosInstanceUseOnlyIfNeeded,
    type ServerListRequest,
    type ServerListResponse,
} from '@lumapps/base-api';
import { fileReader } from '@lumapps/base-api/utils/files';
import { redundantFetchApi } from '@lumapps/base-api/utils/redundantFetchApi';
import type { Community } from '@lumapps/communities/types';
import { Media, UploadURL } from '@lumapps/medias/types';
import { addQueryParamsToUrl } from '@lumapps/router/utils';
import { downloadFile } from '@lumapps/utils/file/downloadFile';
import type { OneOrMany } from '@lumapps/utils/types/OneOrMany';

import { DOCUMENT_V1_SORTING, type DOCUMENT_TYPES } from '../constants';
import type { DocumentV1, GetDocumentPermissionsResponse, MediaRevisionParams, MediaRevisionResponse } from '../types';

export const documentApiHaussmann = new BaseApi({
    path: 'document',
});

export const documentApi = new BaseApi({
    path: 'document',
    bypassMonolith: false,
});

export const mediasApi = new BaseApi({
    version: BaseApi.versions.v2,
    path: 'medias',
});

export const mediaV1Api = new BaseApi({
    path: 'media',
    bypassMonolith: false,
});

const MAX_TRY = 10;
const DELAY_RETRY = 1000;

/** API parameterized paths. */
export const paths = {
    documents: () => 'documents',
    documentById: (docId: string) => `documents/${docId}`,
    documentsByIds: () => 'documents/get-by-ids',
};

export interface GetDocumentParams {
    docPath: string;
}

/**
 * API /POST on 'document/get' to get document data
 * @param params give docPath params
 */
export const getDocumentData = (params: GetDocumentParams): Promise<BaseApiResponse<Media>> =>
    documentApiHaussmann.post('get', params);

export interface GetMultipleDocumentParams {
    docPaths: string[];
}

/**
 * API /POST on 'document/getMulti' to get multiple document data
 * @param params give docPath list params
 */
export const getMultipleDocumentData = (
    params: GetMultipleDocumentParams,
    signal?: AbortSignal,
): Promise<BaseApiResponse<{ items: DocumentV1[] }>> => documentApiHaussmann.post('getMulti', params, { signal });

export interface DocumentV1CategoryProperties {
    /**
     * The author of documents in this category.
     */
    author?: string;
    /**
     * 'File' if the category only contains files, 'Folder' if the category only contains folders.
     */
    model?: string;
    /**
     * True if this category lists files recursively.
     */
    recursive?: boolean | null;
    /**
     * True if the files in this category are shared.
     */
    shared: boolean;
    /**
     * True if the files in this category are trashed.
     */
    trashed: boolean;
}

export interface GetDocumentPermissionsParams {
    docPath: string;
    searchParameters: DocumentV1CategoryProperties;
}

export interface DownloadRevisionParams {
    mediaId: string;
    revisionId: string;
}

export interface ListDocumentsParams extends Omit<ServerListRequest, 'fields' | 'maxResults'> {
    /**
     * Documents that are children of the item identified by this docPath will be returned.
     */
    docPath: string;
    lang?: string;
    maxResults?: number;
    sortOrder?: OneOrMany<DOCUMENT_V1_SORTING>;
    searchInFolder: boolean;
    /**
     * 	A set of properties for a document category.
     */
    searchParameters: DocumentV1CategoryProperties;
    /**
     * The document tags to search for.
     */
    searchTags: string[];
    /**
     * The text to search for.
     */
    searchText: string;
    /**
     * A list of document types to filter the results.
     */
    searchTypes?: DOCUMENT_TYPES[];
}

/**
 * API /POST on 'document/list' to get all document from folder
 *
 * @param {ListDocumentsParams} params
 * @param {AbortSignal | undefined} signal
 * @returns {Promise<ServerListResponse<DocumentV1>>}
 */
export const listDocuments = async (params: ListDocumentsParams, signal?: AbortSignal) => {
    const sortOrderArray =
        params.sortOrder && !Array.isArray(params.sortOrder) ? [params.sortOrder] : params.sortOrder || [];

    // We force the folder first order here
    const sortOrder = sortOrderArray.includes(DOCUMENT_V1_SORTING.SORT_FOLDER_FIRST)
        ? sortOrderArray
        : [DOCUMENT_V1_SORTING.SORT_FOLDER_FIRST, ...sortOrderArray];

    const { data: documentList } = await documentApiHaussmann.post<ServerListResponse<DocumentV1>>('list', {
        ...params,
        sortOrder,
        signal,
    });

    return documentList;
};

/**
 * Get a document from it's path
 *
 * @param {GetDocumentParams} params
 * @param {AbortSignal | undefined} signal
 * @returns {Promise<DocumentV1>}
 */
export const getDocument = async (params: GetDocumentParams, signal?: AbortSignal) => {
    const { data: document } = await documentApiHaussmann.post<DocumentV1>('get', { ...params, signal });

    return document;
};

export type UpdateDocumentParams = Partial<DocumentV1>;

/**
 * Update a document by its path or ID.
 */
export const updateDocument = async (params: UpdateDocumentParams, signal?: AbortSignal): Promise<DocumentV1> => {
    const { data: updatedDocument } = await documentApiHaussmann.post<DocumentV1>('/update', {
        ...cleanParams(params),
        signal,
    });

    return updatedDocument;
};

export interface MoveDocumentParams {
    /** The doc path of the location where to move the document. */
    destinationPath: string;
    /** The doc path of the document to move. */
    sourcePath: string;
}

/**
 * Move a document
 *
 * @param {MoveDocumentParams} params
 * @returns {Promise<string>}
 */
export const moveDocument = async (params: MoveDocumentParams) => {
    const { data: document } = await documentApiHaussmann.post<string>('/move', { ...cleanParams(params) });

    return document;
};

export interface SaveFolderParams {
    /** The folder description. */
    description?: string;
    /** The folder name. */
    name: string;
    /** Where to create the folder. */
    parentPath: string;
    /**
     * The serialized properties of the category.
     * Will be used if the parameter shared is absent,
     * to determine whether the folder is shared.
     */
    serializedProperties?: string;
    /**
     * True if the folder is shared (in the site library).
     * @default true
     */
    shared?: boolean;
}

/**
 * Create a new folder or edit an existing folder.
 *
 * @param {SaveFolderParams} params
 * @returns {Promise<DocumentV1>}
 */
export const saveFolder = async ({ shared = true, ...params }: SaveFolderParams) => {
    const { data: folder } = await documentApiHaussmann.post<DocumentV1>('/folder/save', {
        ...cleanParams({ shared, ...params }),
    });

    return folder;
};

export interface DeleteDocumentParams {
    /** Path of the document to delete. */
    docPath: string;
}

/**
 * Delete document (move to trash).
 */
export const deleteDocument = async (params: DeleteDocumentParams) => {
    const { data: document } = await documentApiHaussmann.post('/trash', {
        ...cleanParams(params),
    });

    return document;
};

export interface UploadDocumentBaseOptions {
    lang: string;
    parentPath: string;
    shared: boolean;
    success?: string;
    abortController?: AbortController;
}

export interface UploadDocumentOptions extends UploadDocumentBaseOptions {
    file: File;
    fileName?: string;
}

export const uploadDocument = ({ file, abortController, ...params }: UploadDocumentOptions) => {
    const apiParams = {
        fileName: file.name,
        ...params,
    };

    return documentApi
        .post('uploadUrl/get', apiParams, {
            signal: abortController?.signal,
        })
        .then((response) => {
            const formData = new FormData();
            formData.append('file', file);
            const baseConfig = BaseApi.getBaseConfig();

            return dangerousAxiosInstanceUseOnlyIfNeeded.post<ServerListResponse<DocumentV1>>(
                response.data.uploadUrl,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        ...baseConfig.headers,
                    },
                    signal: abortController?.signal,
                },
            );
        });
};

/**
 * Uploads multiple documents using a "fail-fast" strategy.
 * If any single upload fails, the entire batch operation will fail.
 *
 * @param {UploadDocumentOptions[]} config
 * @returns {Promise<AxiosResponse<any, any>[]>}
 */
export const uploadDocuments = (config: UploadDocumentOptions[]) => {
    const uploadPromises = config.map((params) => {
        return uploadDocument(params);
    });

    return Promise.all(uploadPromises);
};

/**
 * Uploads multiple documents and waits for all to complete, regardless of individual success or failure.
 * This function will not reject due to a single failed upload.
 *
 * @param {UploadDocumentOptions[]} config
 * @returns {Promise<PromiseSettledResult<AxiosResponse<any, any>>[]>}
 */
export const uploadDocumentsSettled = (config: UploadDocumentOptions[]) => {
    const uploadPromises = config.map((params) => {
        return uploadDocument(params);
    });

    return Promise.allSettled(uploadPromises);
};

/**
 * IMPORTANT: ONLY USE THIS METHOD IF YOU KNOW WHAT YOU ARE DOING, WE SHOULD NEVER DO POLLING
 * ONLY IF NEEDED. THIS METHOD IS FOR NOW ONLY USED ON THE LEGACY SIDE.
 * @param params UploadDocumentOptions
 * @returns Promise
 */
export const uploadDocumentWithValidation = (params: UploadDocumentOptions) => {
    return uploadDocument(params).then((response) => {
        const { data } = response;
        const { items } = data;

        return redundantFetchApi(
            () =>
                dangerousAxiosInstanceUseOnlyIfNeeded({
                    method: 'GET',
                    url: addQueryParamsToUrl(items[0].content[0].url!, { canUsePlaceholder: 'false' }),
                }).then(() => {
                    return {
                        data: items[0],
                    };
                }),
            MAX_TRY,
            DELAY_RETRY,
        );
    });
};

/**
 * IMPORTANT: ONLY USE THIS METHOD IF YOU KNOW WHAT YOU ARE DOING, WE SHOULD NEVER DO POLLING
 * ONLY IF NEEDED. THIS METHOD IS FOR NOW ONLY USED ON THE LEGACY SIDE.
 * @param config UploadDocumentOptions
 * @returns Promise
 */
export const uploadDocumentsWithValidation = (config: UploadDocumentOptions[]) => {
    const uploadPromises = config.map((params) => {
        return uploadDocumentWithValidation(params);
    });

    return Promise.all(uploadPromises);
};

export interface UploadSecuredDocumentForCommunityOptions extends UploadDocumentOptions {
    community: Pick<Community, 'uid'>;
}

export const uploadSecuredDocumentForCommunity = ({
    community,
    file,
    abortController,
    ...params
}: UploadSecuredDocumentForCommunityOptions) => {
    let uploadUrlResponse: UploadURL;

    const apiParams = {
        fileName: file.name,
        ...params,
    };

    return documentApi
        .post('uploadUrl/get', apiParams, {
            signal: abortController?.signal,
        })
        .then((response) => {
            uploadUrlResponse = response.data;
            return fileReader(file);
        })
        .then((result) =>
            dangerousAxiosInstanceUseOnlyIfNeeded({
                headers: {
                    'Content-Type': file.type,
                    ...uploadUrlResponse.headers,
                },
                method: (uploadUrlResponse.httpMethod as any) || 'POST',
                url: uploadUrlResponse.uploadUrl,
                data: result,
            }),
        )
        .then(() =>
            redundantFetchApi(
                () =>
                    getDocumentData({
                        docPath: `provider=haussmann_media/drive=${community.uid}/resource=${uploadUrlResponse.mediaId}`,
                    }),
                MAX_TRY,
                DELAY_RETRY,
            ),
        );
};

export const getMediaRevisions = async (params: MediaRevisionParams, mediaId: string, signal?: AbortSignal) => {
    const { data } = await mediasApi.get<MediaRevisionResponse>(`${mediaId}/revisions`, {
        params,
        signal,
    });
    return data;
};

export const restoreMediaRevision = async (mediaId: string, revisionId: string) => {
    const { data } = await mediasApi.post(`${mediaId}/revisions/${revisionId}/restore`, {});
    return data;
};

export const getDocumentPermissions = async (params: GetDocumentPermissionsParams, signal?: AbortSignal) => {
    const { data } = await documentApiHaussmann.post<GetDocumentPermissionsResponse>('getPermissions', {
        ...params,
        signal,
    });
    return data;
};

export const downloadRevision = async (params: DownloadRevisionParams) => {
    const response = await mediasApi.get(`${params.mediaId}/revisions/${params.revisionId}/permalink`, {
        responseType: 'blob',
        params: {
            dl: true,
        },
    });

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'download';

    if (contentDisposition) {
        const utf8Match = contentDisposition.match(/filename\*=utf-8''(.+)/i);
        const regularMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);

        if (utf8Match?.[1]) {
            filename = decodeURIComponent(utf8Match[1]);
        } else if (regularMatch?.[1]) {
            // eslint-disable-next-line prefer-destructuring
            filename = regularMatch[1];
        }
    }

    downloadFile(response.data, filename);
};

export const deleteMedia = async (mediaUid: string) => {
    const { data } = await mediaV1Api.delete(`delete?uid=${mediaUid}`);
    return data;
};

export const emptyTrash = async (docPath: string) => {
    const { data } = await documentApi.post('trash/empty', { docPath });
    return data;
};

export const restoreMedia = async (docPath: string) => {
    const { data } = await documentApi.post('restore', { docPath });
    return data;
};

export const revisionsQueryKeys = {
    all: () => ['medias'] as const,
    revisionsList: (params: MediaRevisionParams, mediaId: string) =>
        [...revisionsQueryKeys.all(), mediaId, 'revisions', params] as const,
};

export const documentsQueryKeys = {
    all: () => [paths.documents()] as const,
    lists: () => [...documentsQueryKeys.all(), 'list'] as const,
    list: (params: ListDocumentsParams) => [...documentsQueryKeys.lists(), params] as const,
    getDocument: (params?: GetDocumentParams) => [...documentsQueryKeys.all(), 'get', params] as const,
    getPermissions: (params?: GetDocumentPermissionsParams) =>
        [...documentsQueryKeys.all(), 'getPermissions', params] as const,
    getDocumentsByDocPaths: (params?: GetMultipleDocumentParams) =>
        [...documentsQueryKeys.all(), 'get-by-doc-paths', params] as const,
};
